/**
 * Client for the gateway's `POST /api/v1/visualize` SSE endpoint —
 * operate-ui side. Mirrors the cognos-plugin's `askVisualize` but
 * uses operate-ui's `getAuthToken` for auth resolution.
 *
 * The same backend serves both UIs, so this is a verbatim port with
 * the import paths swapped.
 */
import { getAuthToken } from "../api";
import type {
  VisualizeRequestBody, VizChoicePayload, VizCompletePayload,
  VizErrorPayload, VizPhasePayload, VizRepairPayload, VizStreamEvent,
  VizTelemetryPayload, VizValidationPayload,
} from "./types";

export interface AskVisualizeOptions {
  /** Bearer token override; falls back to {@link getAuthToken}. */
  token?: string;
  /** Override gateway URL — mostly for tests; production uses the /api/v1 prefix. */
  gatewayUrl?: string;
  /** Abort the in-flight stream. */
  signal?: AbortSignal;
}

/**
 * Async iterator yielding {@link VizStreamEvent}s as the gateway emits
 * them. Always finishes with a `done` or `error` event so callers can
 * release resources cleanly.
 */
export async function* askVisualize(
  body: VisualizeRequestBody,
  options: AskVisualizeOptions = {},
): AsyncIterable<VizStreamEvent> {
  const gateway = options.gatewayUrl ?? "";   // empty → use relative /api/v1
  const wireBody = toWireFormat(body);
  const token = options.token ?? getAuthToken();

  let resp: Response;
  try {
    resp = await fetch(`${gateway}/api/v1/visualize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept:         "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-DatumCraft-Client": "operate-ui",
      },
      body: JSON.stringify(wireBody),
      signal: options.signal,
      credentials: "include",
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "fetch failed";
    yield { type: "error", payload: { phase: "transport", reason: "fetch.failed", message: msg } };
    yield { type: "done" };
    return;
  }

  if (!resp.ok || !resp.body) {
    yield {
      type: "error",
      payload: {
        phase: "transport",
        reason: "http.status",
        message: `Gateway returned ${resp.status}`,
      },
    };
    yield { type: "done" };
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let sawDone = false;

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        let eventName = "message";
        const dataParts: string[] = [];
        for (const raw of frame.split("\n")) {
          if (raw.startsWith("event:")) eventName = raw.slice(6).trim();
          else if (raw.startsWith("data:")) dataParts.push(raw.slice(5).replace(/^ /, ""));
        }
        if (dataParts.length === 0) continue;
        const payload = dataParts.join("\n");
        let json: unknown;
        try { json = JSON.parse(payload); } catch { continue; }

        const evt = mapVizEvent(eventName, json);
        if (evt) {
          yield evt;
          if (evt.type === "done") sawDone = true;
        }
      }
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : "stream interrupted";
    yield {
      type: "error",
      payload: { phase: "transport", reason: "stream.interrupted", message: msg },
    };
  }

  if (!sawDone) {
    yield { type: "done" };
  }
}

/**
 * Translate a typed body into the JSON shape the backend's
 * {@code VisualizeRequest} record expects.
 */
function toWireFormat(body: VisualizeRequestBody): Record<string, unknown> {
  const out: Record<string, unknown> = {
    intent:    body.intent,
    sessionId: body.sessionId ?? null,
    constraints: {
      preferredFormat: body.preferredFormat ?? null,
      brandPalette:    body.brandPalette ?? null,
    },
  };
  if (body.source.kind === "inline") {
    out.dataset = { columns: body.source.columns, rows: body.source.rows };
    out.datasetRef = null;
  } else {
    out.dataset = null;
    out.datasetRef = { kind: body.source.refKind, params: body.source.params };
  }
  return out;
}

/**
 * Map a single named SSE frame to a {@link VizStreamEvent}. Unknown
 * event names are ignored rather than fatal — backend can add new
 * event names without breaking older clients.
 */
export function mapVizEvent(eventName: string, data: unknown): VizStreamEvent | null {
  const obj = (data ?? {}) as Record<string, unknown>;
  switch (eventName) {
    case "viz_phase": {
      const phase = typeof obj.phase === "string" ? obj.phase : "";
      const status = obj.status === "started" || obj.status === "completed" || obj.status === "failed"
        ? obj.status
        : null;
      const message = typeof obj.message === "string" ? obj.message : null;
      if (!phase || !status) return null;
      return { type: "phase", payload: { phase, status, message } as VizPhasePayload };
    }
    case "viz_choice": {
      const format = obj.format;
      if (format !== "vega-lite" && format !== "echarts" && format !== "deck-gl" && format !== "mermaid") return null;
      const vizType    = typeof obj.vizType === "string" ? obj.vizType : "";
      const rationale  = typeof obj.rationale === "string" ? obj.rationale : "";
      const confidence = typeof obj.confidence === "number" ? obj.confidence : 0.0;
      if (!vizType) return null;
      return { type: "choice", payload: { format, vizType, rationale, confidence } as VizChoicePayload };
    }
    case "viz_validation": {
      const valid      = obj.valid === true;
      const errorCount = typeof obj.errorCount === "number" ? obj.errorCount : 0;
      const errorsRaw  = Array.isArray(obj.errors) ? obj.errors : [];
      const errors     = errorsRaw
        .filter((e): e is Record<string, unknown> => Boolean(e) && typeof e === "object")
        .map((e) => ({
          path:     typeof e.path === "string" ? e.path : "",
          message:  typeof e.message === "string" ? e.message : "",
          severity: typeof e.severity === "string" ? e.severity : "error",
        }));
      return { type: "validation", payload: { valid, errorCount, errors } as VizValidationPayload };
    }
    case "viz_repair": {
      const cycle = typeof obj.cycle === "number" ? obj.cycle : 0;
      const maxCycles = typeof obj.maxCycles === "number" ? obj.maxCycles : 0;
      const reason = typeof obj.reason === "string" ? obj.reason : "";
      return { type: "repair", payload: { cycle, maxCycles, reason } as VizRepairPayload };
    }
    case "viz_telemetry": {
      const phase = typeof obj.phase === "string" ? obj.phase : "";
      const fields = obj.fields && typeof obj.fields === "object" && !Array.isArray(obj.fields)
        ? obj.fields as Record<string, string>
        : {};
      if (!phase) return null;
      return { type: "telemetry", payload: { phase, fields } as VizTelemetryPayload };
    }
    case "viz_complete": {
      const format = obj.format;
      if (format !== "vega-lite" && format !== "echarts" && format !== "deck-gl" && format !== "mermaid") return null;
      const vizType  = typeof obj.vizType === "string" ? obj.vizType : "";
      const specJson = typeof obj.specJson === "string" ? obj.specJson : "";
      const rows     = Array.isArray(obj.rows) ? obj.rows as Array<Array<unknown>> : [];
      const cols     = Array.isArray(obj.columnNames)
        ? (obj.columnNames as unknown[]).filter((c): c is string => typeof c === "string")
        : [];
      if (!vizType || !specJson) return null;
      // Candidates are optional; older backends won't ship the field, so
      // default to empty list rather than failing the whole event.
      const candidates: VizCompletePayload["candidates"] = [];
      if (Array.isArray(obj.candidates)) {
        for (const c of obj.candidates as unknown[]) {
          if (!c || typeof c !== "object") continue;
          const co = c as Record<string, unknown>;
          const fmt = co.format;
          if (fmt !== "vega-lite" && fmt !== "echarts" && fmt !== "deck-gl" && fmt !== "mermaid") continue;
          const vt = typeof co.vizType  === "string" ? co.vizType  : "";
          const sj = typeof co.specJson === "string" ? co.specJson : "";
          const lb = typeof co.label    === "string" ? co.label    : vt;
          const ih = typeof co.iconHint === "string" ? co.iconHint : vt;
          if (!vt || !sj) continue;
          candidates.push({ format: fmt, vizType: vt, specJson: sj, label: lb, iconHint: ih });
        }
      }
      // B0 (save-viz Feature B): optional source provenance. Tolerant
      // parse — older backends won't ship the field.
      let sourceRef: VizCompletePayload["sourceRef"] = null;
      if (obj.sourceRef && typeof obj.sourceRef === "object") {
        const sr = obj.sourceRef as Record<string, unknown>;
        if (typeof sr.kind === "string" && sr.kind !== "") {
          sourceRef = {
            kind:   sr.kind,
            params: sr.params && typeof sr.params === "object"
              ? sr.params as Record<string, unknown>
              : {},
          };
        }
      }
      return { type: "complete", payload: { format, vizType, specJson, rows, columnNames: cols, candidates, sourceRef } as VizCompletePayload };
    }
    case "viz_error": {
      const phase   = typeof obj.phase === "string" ? obj.phase : "unknown";
      const reason  = typeof obj.reason === "string" ? obj.reason : "unknown";
      const message = typeof obj.message === "string" ? obj.message : "";
      return { type: "error", payload: { phase, reason, message } as VizErrorPayload };
    }
    case "done":
      return { type: "done" };
    default:
      return null;
  }
}
